/**
 * BankImportPage — Phase 4b.
 *
 * Upload eines Kontoauszug-CSV (Sparkasse/Volksbank/DKB/N26-Style) →
 * Backend liefert geparste Transaktionen + Auto-Match-Vorschläge gegen
 * offene Rechnungen. User wählt pro Transaktion einen Match aus
 * und bucht das Sammelpaket per "Buchen"-Knopf.
 *
 * Route: /dealer/bank-import
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, FileText, Check, AlertCircle, Loader2, ArrowLeft, Banknote } from "lucide-react";

import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";

const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export default function BankImportPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [selections, setSelections] = useState({});  // tx_index -> invoice_id
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    if (user === false) navigate("/login");
    if (user && user.role !== "b2b" && user.role !== "admin") navigate("/");

    const onUpload = async () => {
        if (!file) { toast.error("Bitte CSV wählen"); return; }
        setBusy(true);
        setPreview(null);
        setResult(null);
        const form = new FormData();
        form.append("file", file);
        try {
            const r = await api.post("/finance/bank-statements/preview", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(r.data);
            // Standard-Auswahl: höchster Score pro Transaktion ≥ 60 vorbelegt
            const pre = {};
            r.data.matches.forEach((m) => {
                if (m.candidates.length > 0 && m.candidates[0].score >= 60) {
                    pre[m.tx_index] = m.candidates[0].invoice_id;
                }
            });
            setSelections(pre);
            toast.success(`${r.data.count} Transaktionen erkannt`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "CSV-Verarbeitung fehlgeschlagen");
        }
        setBusy(false);
    };

    const onCommit = async () => {
        const matches = preview.matches
            .filter((m) => selections[m.tx_index])
            .map((m) => {
                const inv = m.candidates.find((c) => c.invoice_id === selections[m.tx_index]);
                return {
                    invoice_id: selections[m.tx_index],
                    amount: Math.min(m.tx.amount, inv?.remaining || m.tx.amount),
                    tx_date: m.tx.date,
                    tx_purpose: m.tx.purpose,
                    tx_counterparty: m.tx.counterparty,
                };
            });
        if (matches.length === 0) { toast.error("Keine Buchung ausgewählt"); return; }
        if (!window.confirm(`${matches.length} Buchung(en) in Rechnungen übernehmen?`)) return;
        setBusy(true);
        try {
            const r = await api.post("/finance/bank-statements/commit", {
                matches,
                file_name: preview.file_name,
            });
            setResult(r.data);
            toast.success(`${r.data.count_booked} Zahlung(en) gebucht`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen");
        }
        setBusy(false);
    };

    return (
        <div className="min-h-screen bg-gray-50" data-testid="bank-import-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/dealer/invoices" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-invoices">
                        <ArrowLeft className="w-3 h-3" /> Zurück zu Rechnungen
                    </Link>
                    <div className="mt-2 flex items-center gap-3">
                        <Banknote className="w-7 h-7 text-[#FFCC00]" />
                        <div>
                            <h1 className="font-display font-black text-4xl md:text-5xl">Kontoauszug-Import</h1>
                            <p className="text-sm text-gray-300 mt-1">CSV hochladen → automatischer Abgleich mit offenen Rechnungen → ein Klick zum Buchen</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-6">
                {/* Upload */}
                <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="bank-upload-block">
                    <h2 className="font-bold text-base mb-3 flex items-center gap-2"><Upload className="w-4 h-4" /> CSV-Datei hochladen</h2>
                    <div className="flex flex-wrap gap-3 items-center">
                        <input
                            type="file"
                            accept=".csv"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="text-sm"
                            data-testid="bank-file-input"
                        />
                        <Button
                            onClick={onUpload}
                            disabled={busy || !file}
                            className="bg-[#111827] hover:bg-black text-white rounded-sm uppercase tracking-wider"
                            data-testid="bank-upload-btn"
                        >
                            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                            Hochladen & Analysieren
                        </Button>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-2">Unterstützt: Sparkasse / Volksbank / DKB / N26 CSV-Exporte (Delimiter `;` oder `,`). Wir erkennen Buchungstag, Betrag und Verwendungszweck automatisch.</p>
                </div>

                {/* Preview + Matches */}
                {preview && !result && (
                    <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="bank-preview-block">
                        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
                            <div>
                                <div className="font-bold text-base">{preview.count} Transaktionen erkannt</div>
                                <div className="text-[11px] text-gray-500">Eingehend gesamt: <strong className="font-mono-num">{fmtEur(preview.total_in)}</strong></div>
                            </div>
                            <Button onClick={onCommit} disabled={busy} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider" data-testid="bank-commit-btn">
                                {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                                Auswahl buchen ({Object.keys(selections).filter((k) => selections[k]).length})
                            </Button>
                        </div>
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                <tr>
                                    <th className="text-left px-3 py-2">Datum</th>
                                    <th className="text-left px-3 py-2">Verwendungszweck</th>
                                    <th className="text-left px-3 py-2">Auftraggeber</th>
                                    <th className="text-right px-3 py-2">Betrag</th>
                                    <th className="text-left px-3 py-2">Vorschlag</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.matches.map((m) => (
                                    <tr key={m.tx_index} className="border-t border-gray-100" data-testid={`bank-tx-${m.tx_index}`}>
                                        <td className="px-3 py-2 text-xs font-mono-num text-gray-600">{m.tx.date}</td>
                                        <td className="px-3 py-2 text-xs max-w-md truncate">{m.tx.purpose || "—"}</td>
                                        <td className="px-3 py-2 text-xs">{m.tx.counterparty || "—"}</td>
                                        <td className={`px-3 py-2 text-right font-mono-num font-bold ${m.tx.amount > 0 ? "text-green-700" : "text-red-700"}`}>{fmtEur(m.tx.amount)}</td>
                                        <td className="px-3 py-2 text-xs">
                                            {m.candidates.length === 0 ? (
                                                <span className="text-gray-400 italic">Kein Vorschlag</span>
                                            ) : (
                                                <select
                                                    value={selections[m.tx_index] || ""}
                                                    onChange={(e) => setSelections((s) => ({ ...s, [m.tx_index]: e.target.value }))}
                                                    className="border border-gray-300 rounded-sm px-2 py-1 text-xs bg-white min-w-[260px]"
                                                    data-testid={`bank-tx-pick-${m.tx_index}`}
                                                >
                                                    <option value="">— nicht buchen —</option>
                                                    {m.candidates.map((c) => (
                                                        <option key={c.invoice_id} value={c.invoice_id}>
                                                            {c.invoice_number || "(Entwurf)"} · {c.customer_label} · {fmtEur(c.remaining)} offen ({c.score}%)
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Ergebnis */}
                {result && (
                    <div className="bg-white border border-green-200 rounded-sm p-5" data-testid="bank-result-block">
                        <div className="flex items-center gap-2 mb-3">
                            <Check className="w-5 h-5 text-green-700" />
                            <h2 className="font-bold text-base">Buchung abgeschlossen</h2>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-green-50 border border-green-200 rounded-sm p-3 text-center">
                                <div className="text-[10px] uppercase tracking-wider text-gray-600">Gebucht</div>
                                <div className="text-2xl font-black font-mono-num">{result.count_booked}</div>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-center">
                                <div className="text-[10px] uppercase tracking-wider text-gray-600">Fehler</div>
                                <div className="text-2xl font-black font-mono-num">{result.count_errors}</div>
                            </div>
                        </div>
                        {result.booked.length > 0 && (
                            <ul className="border border-gray-200 rounded-sm divide-y">
                                {result.booked.map((b) => (
                                    <li key={b.bank_payment_id} className="px-3 py-2 text-xs flex items-center justify-between">
                                        <div>
                                            <span className="font-bold">{b.invoice_number}</span>
                                            <span className="ml-2 text-gray-500">→ Status {b.new_status}</span>
                                        </div>
                                        <span className="font-mono-num font-bold text-green-700">{fmtEur(b.amount)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {result.errors.length > 0 && (
                            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-sm p-3">
                                <div className="text-[11px] font-bold uppercase tracking-wider text-amber-900 mb-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Fehler</div>
                                <ul className="text-[11px] text-amber-900 space-y-0.5">
                                    {result.errors.map((er, i) => (
                                        <li key={i}>{er.invoice_id}: {er.error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div className="mt-4 flex gap-2 justify-end">
                            <Button onClick={() => { setFile(null); setPreview(null); setSelections({}); setResult(null); }} variant="outline" className="rounded-sm" data-testid="bank-reset">
                                Weitere CSV importieren
                            </Button>
                            <Link to="/dealer/invoices">
                                <Button className="bg-[#111827] hover:bg-black text-white rounded-sm uppercase tracking-wider">
                                    <FileText className="w-3 h-3 mr-1" /> Zu den Rechnungen
                                </Button>
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
